//
// # Orientation
//
// Including reflection & 90 degree increment rotations
//
// Rules for `Orientation`:
// - Reflection is vertical, i.e. about the x-axis
// - Rotation is counter-clockwise, i.e. positive
// - Reflection is applied *before* rotation
//

// Local Imports
import { Result, exhaust } from "../errors";
import { Rotation } from "./rotation";
import { Matrix } from "./matrix";

// # Orientation
// including reflection & 90 degree increment rotation
export interface Orientation {
  reflected: boolean; // Vertical reflection, applied before rotation
  rotation: Rotation; // Rotation in increments of 90 degrees
}

// The "impl" of orientation-related functions
export const Orientation = {
  //
  // Create a new `Orientation`
  new: (reflected: boolean, rotation: Rotation): Orientation => {
    return { reflected, rotation };
  },

  eq: (a: Orientation, b: Orientation): boolean => {
    return a.reflected === b.reflected && a.rotation === b.rotation;
  },

  // Clone an existing `Orientation`
  clone: (orientation: Orientation): Orientation => {
    const { reflected, rotation } = orientation;
    return { reflected, rotation };
  },

  // Default orientation
  // No reflection, no rotation
  default: (): Orientation => ({ reflected: false, rotation: Rotation.R0 }),

  relative: (
    orientation: Orientation,
    relativeTo: Orientation
  ): Orientation => {
    const reflected = orientation.reflected !== relativeTo.reflected;
    const rotation = Rotation.relative(
      orientation.rotation,
      relativeTo.rotation
    );
    return { reflected, rotation };
  },

  // Get the inverse orientation
  inverse: (orientation: Orientation): Orientation => {
    return {
      reflected: orientation.reflected, // Reflection is its own inverse
      rotation: Rotation.inverse(orientation.rotation),
    };
  },

  toMatrix: (orientation: Orientation): Matrix => {
    switch (orientation.reflected) {
      case false: {
        switch (orientation.rotation) {
          case Rotation.R0:
            return theEightValidMatrices.R0;
          case Rotation.R90:
            return theEightValidMatrices.R90;
          case Rotation.R180:
            return theEightValidMatrices.R180;
          case Rotation.R270:
            return theEightValidMatrices.R270;
          default:
            exhaust(orientation.rotation);
        }
      }
      case true: {
        switch (orientation.rotation) {
          case Rotation.R0:
            return Matrix.new(1, 0, 0, -1);
          case Rotation.R90:
            return Matrix.new(0, 1, 1, 0);
          case Rotation.R180:
            return Matrix.new(-1, 0, 0, 1);
          case Rotation.R270:
            return Matrix.new(0, -1, -1, 0);
          default:
            exhaust(orientation.rotation);
        }
      }
      default:
        exhaust(orientation.reflected);
    }
  },
};

//
// # NOTE:
//
// There are really a total of eight valid orientation matrices, paired with the eight values of `Orientation`.
// It would be great if TS/ JS let us define a mapping, hashmap, dictionary, or similar between the two.
// Because both are objects and TS/ JS really, really prefer "identity equality" for comparing keys,
// just writing these two functions turns out aways easier than trying to define a mapping.
//
const theEightValidMatrices = {
  R0: Matrix.identity(),
  R90: Matrix.new(0, 1, -1, 0),
  R180: Matrix.new(-1, 0, 0, -1),
  R270: Matrix.new(0, -1, 1, 0),
  // Reflected
  XR0: Matrix.new(1, 0, 0, -1),
  XR90: Matrix.new(0, 1, 1, 0),
  XR180: Matrix.new(-1, 0, 0, 1),
  XR270: Matrix.new(0, -1, -1, 0),
};
